import MarkdownPreview from '@uiw/react-markdown-preview';
import { motion } from 'framer-motion';
import { Eye, Image as ImageIcon, Loader2, MessageSquare, Upload, X } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useTranslate } from 'sbx-crm-menu-ui';
import NavbarBreadcrumbV2 from 'src/components/shared/NavbarBreadcrumbV2';
import { imageQuestionService } from '../../../services/ImageService';
import { formatSizeUnits } from '../../../utils';
import { Button } from '../../ui/button';
import { Card, CardContent, CardTitle } from '../../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { ScrollArea } from '../../ui/scroll-area';
import { Textarea } from '../../ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';


export const ImagesPageComponent = () => {

    const [files, setFiles] = useState<File[]>([]);
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t: common } = useTranslate('common');
    const { t: agent } = useTranslate('agent');
    // const { t: routes } = useTranslate('routes');
    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const newFiles = Array.from(event.target.files).map(file => file);
            setFiles(prevFiles => [...prevFiles, ...newFiles]);
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (event.dataTransfer.files) {
            const newFiles = Array.from(event.dataTransfer.files).map(file => file);
            setFiles(prevFiles => [...prevFiles, ...newFiles]);
        }
    };

    const handleSubmit = async () => {
        if (question.trim()) {
            setLoading(true);
            const res = await imageQuestionService({
                question: `${question} \n (Important Note: Always Return your answer on a simple beautifully markdown format)`,
                images: files,
            });
            if (res?.success) {
                setAnswer(res.result);
                setLoading(false);
            } else {
                setLoading(false);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 p-4 sm:p-8">
            <NavbarBreadcrumbV2 routes={[
                    {
                        label: common('image')
                    }
                ]} 
            />
            <Card
                className="w-full max-w-8xl  mt-5 mx-auto bg-white bg-opacity-90 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden">
                <CardContent className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-4 ">
                    <div>
                        <div className="flex items-center space-x-4">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <motion.div
                                            className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center cursor-pointer hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                            whileHover={{ rotate: 15 }}
                                            whileTap={{ scale: 0.95 }}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={handleDrop}
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <Upload className="h-6 w-6 text-white" />
                                            <input
                                                type="file"
                                                className="hidden"
                                                onChange={handleFileUpload}
                                                multiple
                                                accept="image/*"
                                                ref={fileInputRef}
                                            />
                                        </motion.div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{common('drag_and_drop')}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <div className="flex-grow">
                                <CardTitle
                                    className="text-lg font-semibold text-purple-700 mb-2">{common('files')}</CardTitle>
                                <ScrollArea className="h-32 w-full rounded-md border border-gray-200 p-2">
                                    {files.length === 0 ? (
                                        <p className="text-gray-500 text-sm">{agent('click_icon_upload_files')}</p>
                                    ) : (
                                        <ul className="space-y-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 items-start">
                                            {files.map((file, index) => (
                                                <motion.li
                                                    key={index}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: 20 }}
                                                    transition={{ duration: 0.3 }}
                                                    className="flex flex-col mt-0 items-center justify-between bg-purple-50 rounded-lg p-12text-sm"
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <ImageIcon className="h-4 w-4 text-purple-500" />
                                                        <span
                                                            className="text-gray-700 truncate max-w-[150px]">{file.name}</span>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                  <span
                                                      className="text-xs text-gray-500">{formatSizeUnits(file.size)}</span>
                                                        <Dialog>
                                                            <DialogTrigger asChild>
                                                                <Button variant="ghost" size="sm" className="p-1">
                                                                    <Eye className="h-4 w-4 text-purple-500" />
                                                                </Button>
                                                            </DialogTrigger>
                                                            <DialogContent className="sm:max-w-[425px]">
                                                                <DialogHeader>
                                                                    <DialogTitle>{file.name}</DialogTitle>
                                                                </DialogHeader>
                                                                <div className="mt-2">
                                                                    <img src={URL.createObjectURL(file)} alt={file.name}
                                                                         className="w-full h-auto rounded-lg" />
                                                                </div>
                                                            </DialogContent>
                                                        </Dialog>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-5 w-5 p-0 rounded-full hover:bg-red-100"
                                                            onClick={() => setFiles(files.filter((_, i) => i !== index))}
                                                        >
                                                            <X className="h-3 w-3 text-red-500" />
                                                        </Button>
                                                    </div>
                                                </motion.li>
                                            ))}
                                        </ul>
                                    )}
                                </ScrollArea>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <CardTitle
                                className="text-lg font-semibold text-purple-700">{agent('ask_a_question')}</CardTitle>
                            <div className="space-y-2">
                                <Textarea
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    placeholder={agent('question_placeholder')}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleSubmit();
                                        }
                                    }}
                                    className="w-full border-2 border-purple-300 focus:border-purple-500 rounded-xl py-2 px-4 resize-none"
                                    rows={3}
                                />
                                <Button
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-4 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                                >
                                    {loading ? (
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    ) : (
                                        <MessageSquare className="mr-2 h-5 w-5" />
                                    )}

                                    {loading ? agent('processing') : common('send')}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div>
                        <CardTitle className="text-lg font-semibold text-purple-700 mb-2">{agent('answer')}</CardTitle>
                        <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
                            <CardContent className="p-0">
                                <div
                                    className={`${answer.length > 0 ? 'max-h-[80vh]' : 'h-32'}  w-full rounded-md overflow-auto`}>
                                    {answer ?
                                        <div className="">
                                            <MarkdownPreview className={''}
                                                             style={{ padding: 16 }}
                                                             source={answer.replaceAll("```markdown\n", "").replaceAll("```", "")} />
                                        </div> :
                                            <p className="text-gray-400 p-4">
                                                {loading ? `${agent('processing')}...` : agent('empty_answer')}
                                            </p>}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
